import { useState } from "react";
import {
  WithCUToggleCommandsAndData,
  WithData,
  WithRecordID,
} from "../../../models/shared/CommonModels";
import { useMutation, useQueryClient } from "react-query";
import { AxiosError } from "axios";
import {
  fromAxiosErrorToMessage,
  prepareCreateData,
  prepareEditData,
} from "../../../utils/crud";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import ErrorMessageAlertComponent from "../../errors/ErrorMessageAlertComponent";
import { REQUIRED_HELPER_TEXT } from "../../../utils/form";
import { fetchBrandBeneSelectQueryKey } from "../../../utils/QueryClient";
import {
  BeneBrandModel,
  BeneBrandPostModel,
  BeneBrandPutModel,
  BeneBrandRequest,
} from "../../../models/api/response/bene/BeneBrandModel";
import { createBrandBene, updateBrand } from "../../../api/bene/brand";

const BeneBrandFormComponent: React.FC<
  WithCUToggleCommandsAndData<BeneBrandModel>
> = ({ editEnabled, onClose, initialData, open, toggleOpen }) => {
  const queryClient = useQueryClient();

  const [hideError, setHideError] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const [nome, setNome] = useState<string>(initialData.nome || "");
  const [descrizione, setDescrizione] = useState<string>(
    initialData.descrizione || ""
  );

  const [error, setError] = useState<boolean>(false);

  const resetFields = () => {
    setNome("");
    setDescrizione("");
    setHideError(true);
  };

  const createMutation = useMutation({
    mutationFn: (x: WithData) => createBrandBene(x),
    onSuccess: () => {
      queryClient.invalidateQueries(fetchBrandBeneSelectQueryKey);
      resetFields();
      if (onClose !== undefined) onClose();
    },
    onError: (err: AxiosError) => {
      setHideError(false);
      setErrorMessage(fromAxiosErrorToMessage(err));
    },
  });

  const editMutation = useMutation({
    mutationFn: (x: WithRecordID) => updateBrand(x),
    onSuccess: () => {
      queryClient.invalidateQueries(fetchBrandBeneSelectQueryKey);
    },
    onError: (err: AxiosError) => {
      setHideError(false);
      setErrorMessage(fromAxiosErrorToMessage(err));
    },
  });

  const closeActions = () => {
    toggleOpen();
    if (!hideError) {
      queryClient.invalidateQueries(fetchBrandBeneSelectQueryKey);
    }
  };

  const handleSubmit = () => {
    if (editEnabled) {
      if (initialData === undefined) {
        return;
      }

      const benePut = {} as BeneBrandPutModel;
      benePut.nome = nome;
      benePut.descrizione = descrizione;

      const request = {} as BeneBrandRequest;
      request.brandbene = benePut;

      editMutation.mutate(prepareEditData(initialData.id, request));
    } else {
      const benePost = {} as BeneBrandPostModel;
      benePost.nome = nome;
      benePost.descrizione = descrizione;

      const request = {} as BeneBrandRequest;
      request.brandbene = benePost;

      createMutation.mutate(prepareCreateData(request));
    }

    toggleOpen();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={closeActions}
        PaperProps={{
          component: "form",
          onSubmit: (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
          },
        }}
      >
        <DialogTitle>
          {editEnabled ? "Modifica" : "Crea Nuovo"} Brand
        </DialogTitle>
        <DialogContent style={{ width: 500 }}>
          <ErrorMessageAlertComponent
            error_message={errorMessage}
            hide={() => {
              setHideError(true);
            }}
            visible={!hideError}
          />

          <Typography sx={{ fontSize: "12px", color: "grey" }}>
            {REQUIRED_HELPER_TEXT}
          </Typography>

          <TextField
            error={error ? error : undefined}
            margin="normal"
            required={true}
            label="Nome"
            name="nome"
            multiline
            fullWidth
            value={nome}
            onChange={(e) => {
              setError(false);
              setNome(e.target.value);
            }}
          />

          <TextField
            error={error ? error : undefined}
            margin="normal"
            required={false}
            label="Descrizione"
            name="descrizione"
            multiline
            fullWidth
            value={descrizione}
            onChange={(e) => {
              setError(false);
              setDescrizione(e.target.value);
            }}
          />
        </DialogContent>
        <DialogActions
          sx={{
            paddingLeft: "18pt",
            paddingRight: "18pt",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ justifyContent: "flex-start" }}>
            <Button
              onClick={() => {
                if (onClose !== undefined) {
                  onClose();
                }

                if (!editEnabled) {
                  resetFields();
                }

                closeActions();
              }}
            >
              Chiudi
            </Button>
          </Box>
          <Box sx={{ justifyContent: "flex-end" }}>
            <Button onClick={() => handleSubmit()} variant="contained">
              {editEnabled ? "Salva" : "Crea"}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BeneBrandFormComponent;
